import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Youtube, Plus, Trash2, Loader2, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function PodcastManager() {
  const [videos, setVideos] = useState([]);
  const [url, setUrl] = useState('');
  const [adding, setAdding] = useState(false);

  const load = () => axios.get(`${API_URL}/api/admin/podcast-videos`, { withCredentials: true })
    .then((r) => setVideos(r.data.videos || [])).catch(() => toast.error('Failed to load podcast videos'));
  useEffect(() => { load(); }, []);

  const add = async () => {
    setAdding(true);
    try {
      await axios.post(`${API_URL}/api/admin/podcast-videos`, { url: url.trim() }, { withCredentials: true });
      toast.success('Video added to /podcast');
      setUrl('');
      load();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Please paste a valid YouTube link');
    } finally {
      setAdding(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Remove this video from the podcast page?')) return;
    await axios.delete(`${API_URL}/api/admin/podcast-videos/${id}`, { withCredentials: true });
    toast.success('Video removed');
    load();
  };

  return (
    <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6" data-testid="podcast-manager">
      <div className="flex items-center justify-between gap-4 mb-1">
        <h3 className="text-xl font-bold text-white flex items-center gap-2"><Youtube className="w-5 h-5 text-[#C41E3A]" /> ShiftHappens Podcast</h3>
        <a href="/podcast" target="_blank" rel="noopener noreferrer" className="text-sm text-zinc-400 hover:text-white flex items-center gap-1" data-testid="podcast-view-page-link">View page <ExternalLink className="w-3.5 h-3.5" /></a>
      </div>
      <p className="text-sm text-zinc-400 mb-4">Paste a YouTube video or Shorts link. The newest 6 are shown on /podcast; the rest stay on the channel.</p>
      <div className="flex gap-2 mb-4">
        <input
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && url.trim() && add()}
          placeholder="https://youtube.com/shorts/... or https://youtu.be/..."
          className="flex-1 bg-zinc-800 border border-zinc-700 text-white rounded-lg px-3 py-2 text-sm"
          data-testid="podcast-url-input"
        />
        <button type="button" onClick={add} disabled={adding || !url.trim()} className="px-4 py-2 text-sm rounded-lg bg-[#C41E3A] text-white flex items-center gap-1 disabled:opacity-50" data-testid="podcast-add-btn">
          {adding ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Add
        </button>
      </div>
      <div className="divide-y divide-zinc-800">
        {videos.map((v, i) => (
          <div key={v.id} className="flex items-center gap-4 py-3" data-testid={`admin-podcast-${v.youtube_id}`}>
            <img src={v.thumbnail} alt="" className="w-24 aspect-video object-cover rounded" />
            <div className="flex-1 min-w-0">
              <p className="text-white text-sm truncate">{v.title}</p>
              <p className="text-xs text-zinc-500">{v.is_short ? 'Short' : 'Video'}{i >= 6 ? ' · not shown (only newest 6)' : ''}</p>
            </div>
            <button type="button" onClick={() => remove(v.id)} className="p-2 rounded-lg hover:bg-red-500/10 text-red-400" data-testid={`admin-podcast-delete-${v.youtube_id}`}><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
      </div>
    </div>
  );
}
